/**
 * SalesTimelinePage Component
 *
 * Main page for the Sales Timeline feature. Displays demand vs supply
 * visualisation across different time periods with strain filtering.
 *
 * View modes:
 * - 7d: Single week view
 * - 14d: Double decker (two weeks stacked)
 * - month: Quadro decker (four weeks stacked)
 *
 * US-PE-006: Sales Timeline - Phase 3 Page Components
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, RefreshCw, TrendingUp, BarChart3, Table2, ChevronRight, ChevronDown } from 'lucide-react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { createDate } from '@/utils/dateUtils'
import { useTimelineData } from '@/hooks/useTimelineData'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { formatWeekLabel } from '@/utils/timeline-utils'

import TimelineControls from './TimelineControls'
import TimelineNavigation from './TimelineNavigation'
import SingleWeekView from './SingleWeekView'
import DoubleWeekView from './DoubleWeekView'
import MonthView from './MonthView'
import MobileTimelineSummary from './MobileTimelineSummary'

/**
 * Calculate new anchor date based on navigation direction and view mode
 *
 * @param {Date} currentAnchor - Current anchor date
 * @param {string} direction - 'prev' | 'next' | 'today'
 * @param {string} viewMode - '7d' | '14d' | 'month'
 * @returns {Date} New anchor date
 */
function calculateNewAnchor(currentAnchor, direction, viewMode) {
  if (direction === 'today') {
    return createDate()
  }

  const anchor = createDate(currentAnchor.getTime())
  const multiplier = direction === 'prev' ? -1 : 1

  switch (viewMode) {
    case '7d':
      anchor.setDate(anchor.getDate() + (7 * multiplier))
      break
    case '14d':
      anchor.setDate(anchor.getDate() + (14 * multiplier))
      break
    case 'month':
      // Navigate by calendar month
      anchor.setMonth(anchor.getMonth() + multiplier)
      break
    default:
      anchor.setDate(anchor.getDate() + (7 * multiplier))
  }

  return anchor
}

/**
 * SalesTimelinePage - Main Sales Timeline page component
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function SalesTimelinePage({ user, onLogout }) {
  const navigate = useNavigate()

  // Responsive detection
  const isMobile = useMediaQuery('(max-width: 768px)')

  // State
  const [anchorDate, setAnchorDate] = useState(() => createDate())
  const [viewMode, setViewMode] = useState('7d')
  const [selectedStrainId, setSelectedStrainId] = useState(null)
  const [displayMode, setDisplayMode] = useState('chart') // 'chart' or 'table'

  // Fetch timeline data
  const {
    data,
    weeklyData,
    strains,
    dateRange,
    isLoading,
    error,
    refetch
  } = useTimelineData({
    anchorDate,
    viewMode,
    strainId: selectedStrainId
  })

  // Derived state
  const showAllStrains = selectedStrainId === null

  // Navigation handler
  const handleNavigate = useCallback((direction) => {
    setAnchorDate(prev => calculateNewAnchor(prev, direction, viewMode))
  }, [viewMode])

  // View mode change handler
  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode)
  }, [])

  // Strain change handler
  const handleStrainChange = useCallback((strainId) => {
    setSelectedStrainId(strainId)
  }, [])

  // Generate week label for single week view
  const weekLabel = viewMode === '7d' && dateRange?.start && dateRange?.end
    ? formatWeekLabel(dateRange.start, dateRange.end)
    : undefined

  // Render loading state
  if (isLoading && !data?.length) {
    return (
      <PageLayout
        title="Sales Timeline"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="text-sm text-slate-500">Loading timeline data...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  // Render error state
  if (error) {
    return (
      <PageLayout
        title="Sales Timeline"
        user={user}
        onLogout={onLogout}
      >
        <Alert variant="destructive" className="max-w-lg mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading timeline</AlertTitle>
          <AlertDescription className="mt-2">
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={refetch}
              className="mt-4 w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Sales Timeline"
      subtitle="Demand vs supply forecast by week and strain"
      user={user}
      onLogout={onLogout}
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/sales/predictions')}
            className="hidden sm:inline-flex"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Prediction Model
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Controls */}
        <TimelineControls
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          selectedStrainId={selectedStrainId}
          onStrainChange={handleStrainChange}
          strains={strains}
        />

        {/* Navigation */}
        <TimelineNavigation
          dateRange={dateRange}
          viewMode={viewMode}
          onNavigate={handleNavigate}
          disabled={isLoading}
        />

        {/* Display mode toggle */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1 w-fit">
          <Button
            variant={displayMode === 'chart' ? 'default' : 'ghost'}
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => setDisplayMode('chart')}
          >
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
            Chart
          </Button>
          <Button
            variant={displayMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => setDisplayMode('table')}
          >
            <Table2 className="h-3.5 w-3.5 mr-1.5" />
            Table
          </Button>
        </div>

        {/* Content */}
        <div className="mt-2">
          {displayMode === 'table' ? (
            /* Table view */
            <ForecastTable data={data} strains={strains} />
          ) : isMobile ? (
            /* Mobile: Show weekly summary cards */
            <MobileTimelineSummary
              weeklyData={weeklyData}
              strains={strains}
              showAllStrains={showAllStrains}
              dateRange={dateRange}
            />
          ) : (
            /* Desktop: Show chart views based on view mode */
            <>
              {viewMode === '7d' && (
                <SingleWeekView
                  data={data}
                  strains={strains}
                  showAllStrains={showAllStrains}
                  selectedStrainId={selectedStrainId}
                  weekLabel={weekLabel}
                />
              )}

              {viewMode === '14d' && (
                <DoubleWeekView
                  weeklyData={weeklyData}
                  strains={strains}
                  showAllStrains={showAllStrains}
                  selectedStrainId={selectedStrainId}
                  dateRange={dateRange}
                />
              )}

              {viewMode === 'month' && (
                <MonthView
                  weeklyData={weeklyData}
                  strains={strains}
                  showAllStrains={showAllStrains}
                  selectedStrainId={selectedStrainId}
                  dateRange={dateRange}
                />
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
  )
}

/**
 * ForecastTable - Tabular view with expandable per-customer breakdown
 *
 * Two layers:
 * - Macro row: Prophet demand + supply per day (from useTimelineData)
 * - Detail rows: Heuristic per-customer predictions (from sale_suggestions)
 */
function ForecastTable({ data = [], strains = [] }) {
  const [suggestions, setSuggestions] = useState([])
  const [expandedDays, setExpandedDays] = useState(new Set())
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  // Fetch sale_suggestions for the visible date range
  useEffect(() => {
    if (!data.length) return

    const dates = data.map(d => d.date).filter(Boolean)
    if (!dates.length) return

    const startDate = dates[0]
    const endDate = dates[dates.length - 1]

    setLoadingSuggestions(true)
    import('@/supabase-client').then(({ supabase }) => {
      supabase
        .from('sale_suggestions')
        .select('suggested_date, suggested_quantity, confidence_score, customer_id, product_id, customers(name), products(strain)')
        .eq('status', 'pending')
        .gte('suggested_date', startDate)
        .lte('suggested_date', endDate)
        .order('suggested_date')
        .then(({ data: sugData }) => {
          setSuggestions(sugData || [])
          setLoadingSuggestions(false)
        })
        .catch(() => setLoadingSuggestions(false))
    })
  }, [data])

  // Group suggestions by date → customer
  const suggestionsByDate = useMemo(() => {
    const byDate = {}
    suggestions.forEach(s => {
      const date = s.suggested_date
      if (!byDate[date]) byDate[date] = {}
      const customer = s.customers?.name || 'Unknown'
      if (!byDate[date][customer]) byDate[date][customer] = []
      byDate[date][customer].push({
        product: s.products?.strain || 'Unknown',
        quantity: parseFloat(s.suggested_quantity),
        confidence: parseFloat(s.confidence_score)
      })
    })
    return byDate
  }, [suggestions])

  const toggleDay = (date) => {
    setExpandedDays(prev => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }

  // Get unique strain names from supply data
  const strainNames = useMemo(() => {
    const names = new Set()
    data.forEach(day => {
      if (day.supplyByStrain) {
        Object.keys(day.supplyByStrain).forEach(key => {
          if (!key.endsWith('_color') && day.supplyByStrain[key] > 0) names.add(key)
        })
      }
    })
    return Array.from(names).sort()
  }, [data])

  if (!data.length) {
    return <div className="text-center text-sm text-muted-foreground py-8">No data for this period</div>
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const colCount = 5 + strainNames.length

  return (
    <div className="bg-white rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[30px]"></TableHead>
            <TableHead className="w-[80px]">Date</TableHead>
            <TableHead className="w-[50px]">Day</TableHead>
            <TableHead className="text-right">Demand (kg)</TableHead>
            <TableHead className="text-right">Supply (kg)</TableHead>
            <TableHead className="text-right">Gap (kg)</TableHead>
            {strainNames.map(name => (
              <TableHead key={name} className="text-right text-xs">{name}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map(day => {
            const d = new Date(day.date)
            const dow = dayNames[d.getDay()]
            const isWeekend = d.getDay() === 0 || d.getDay() === 6
            const gap = (day.totalSupplyKg || 0) - (day.demandKg || 0)
            const isExpanded = expandedDays.has(day.date)
            const daySuggestions = suggestionsByDate[day.date] || {}
            const hasCustomerData = Object.keys(daySuggestions).length > 0
            const customerTotal = Object.values(daySuggestions).reduce(
              (sum, products) => sum + products.reduce((s, p) => s + p.quantity, 0), 0
            )

            return (
              <>
                {/* Macro row */}
                <TableRow
                  key={day.date}
                  className={`${isWeekend ? 'bg-muted/30' : ''} ${hasCustomerData ? 'cursor-pointer hover:bg-muted/20' : ''}`}
                  onClick={() => hasCustomerData && toggleDay(day.date)}
                >
                  <TableCell className="w-[30px] px-2">
                    {hasCustomerData && (
                      isExpanded
                        ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {d.getDate()} {d.toLocaleDateString('en-GB', { month: 'short' })}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{dow}</TableCell>
                  <TableCell className="text-right font-medium">
                    <div>{day.demandKg > 0 ? day.demandKg.toFixed(1) : '-'}</div>
                    {hasCustomerData && day.demandKg > 0 && customerTotal > 0 && (
                      <div className="text-[10px] text-muted-foreground font-normal">
                        heuristic: {customerTotal.toFixed(1)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {(day.totalSupplyKg || 0) > 0 ? day.totalSupplyKg.toFixed(1) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {day.demandKg > 0 || (day.totalSupplyKg || 0) > 0 ? (
                      <span className={gap >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {gap > 0 ? '+' : ''}{gap.toFixed(1)}
                      </span>
                    ) : '-'}
                  </TableCell>
                  {strainNames.map(name => (
                    <TableCell key={name} className="text-right text-xs text-muted-foreground">
                      {(day.supplyByStrain?.[name] || 0) > 0 ? day.supplyByStrain[name].toFixed(1) : '-'}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Expanded customer detail rows */}
                {isExpanded && Object.entries(daySuggestions).sort().map(([customer, products]) => {
                  const custTotal = products.reduce((s, p) => s + p.quantity, 0)
                  const productSummary = products.map(p => p.product + ': ' + p.quantity.toFixed(1)).join(', ')

                  return (
                    <TableRow key={`${day.date}-${customer}`} className="bg-blue-50/50">
                      <TableCell></TableCell>
                      <TableCell colSpan={2} className="text-xs font-medium text-blue-800 pl-4">
                        {customer}
                      </TableCell>
                      <TableCell className="text-right text-xs text-blue-700">
                        {custTotal.toFixed(1)}
                      </TableCell>
                      <TableCell colSpan={2 + strainNames.length} className="text-xs text-muted-foreground">
                        {productSummary}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </>
            )
          })}

          {/* Totals row */}
          <TableRow className="border-t-2 font-medium bg-muted/50">
            <TableCell></TableCell>
            <TableCell colSpan={2}>Total</TableCell>
            <TableCell className="text-right">
              {data.reduce((s, d) => s + (d.demandKg || 0), 0).toFixed(1)}
            </TableCell>
            <TableCell className="text-right">
              {data.reduce((s, d) => s + (d.totalSupplyKg || 0), 0).toFixed(1)}
            </TableCell>
            <TableCell className="text-right">
              {(() => {
                const totalGap = data.reduce((s, d) => s + (d.totalSupplyKg || 0) - (d.demandKg || 0), 0)
                return (
                  <span className={totalGap >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {totalGap > 0 ? '+' : ''}{totalGap.toFixed(1)}
                  </span>
                )
              })()}
            </TableCell>
            {strainNames.map(name => (
              <TableCell key={name} className="text-right text-xs">
                {data.reduce((s, d) => s + (d.supplyByStrain?.[name] || 0), 0).toFixed(1)}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
